import {
  AfterViewInit,
  Component,
  OnDestroy,
  inject,
  signal,
  effect,
  input,
  output,
  computed,
} from '@angular/core';
import { GestureHandling } from 'leaflet-gesture-handling';
import { StorageService } from '../../services/storage.service';

import { LP_UI_CONFIG } from '../../core/tokens';
import { Sensor } from '../../models/classes/sensor';
import { I18nService } from '../../services/i18n.service';
import { UiService } from '../../services/ui.service';
import { SpinnerComponent } from '../spinner/spinner';
import { GLOBAL_CONSTANT } from '../../constants/global.constant';
import { AssetsService } from '../../services/asset.service';
import { MapBoxProvider } from 'leaflet-geosearch';
import { NzIconModule } from 'ng-zorro-antd/icon';

const L = window['L'];
//const ZOOM_BUILDING_THRESHOLD = 13;

@Component({
  selector: 'app-map',
  templateUrl: './map.html',
  styleUrls: ['./map.scss'],
  imports: [SpinnerComponent, NzIconModule],
})
export class MapComponent implements AfterViewInit, OnDestroy {
  private readonly cfg = inject(LP_UI_CONFIG);
  private ui = inject(UiService);
  public i18n = inject(I18nService);
  public storage = inject(StorageService);

  userLocation = input<{ lat: number; lng: number } | null>(null);
  showSensors = input<boolean>(false);
  sensors = input<Sensor[]>([]);
  center = input<{ lat: number; lng: number } | null>(null);
  position = input<{ lat: number; lng: number } | null>(null);
  showPosition = input<boolean>(false);
  satelliteView = input<boolean>(false);
  private assetService = inject(AssetsService);

  markerClick = output<Sensor>();
  positionChanged = output<{ lat: number; lng: number }>();

  mapInstance = signal<any>(null);
  isLoading = signal<boolean>(true);
  zoomLevel = signal<number>(GLOBAL_CONSTANT.DEFAULT_INITIALIZER);
  currentCenter = signal<{ lat: number; lng: number }>({
    lat: 51.17599,
    lng: 6.8375,
  });

  isMobile = computed(() => this.ui.IsMobile());

  private tileLayer: any = null;
  private markersLayer: any = null;
  private addedMarker = L.marker([51.17599, 6.8375], {
    icon: L.divIcon({
      className: 'sensor-marker',
      html: '<img src="assets/icons/marker-sensor-o.svg" class="map-marker-icon">',
      iconSize: [1, 1],
      iconAnchor: [0, 0],
    }),
  });

  private userMarker = L.marker([51.17599, 6.8375], {
    icon: L.divIcon({
      className: 'gps-marker',
      html: '',
      iconSize: [1, 1],
      iconAnchor: [0, 0],
    }),
  });
  searchProvider: any;
  keys: any;

  constructor() {
    effect(() => {
      const show = this.showSensors();
      const sensors = this.sensors();
      const showUser = !!this.userLocation();
      const showPos = this.showPosition();

      this.addMarkers(show, sensors, showUser, showPos);
    });

    effect(() => {
      const loc = this.userLocation();
      if (loc) {
        this.userMarker.setLatLng(loc);
        this.currentCenter.set(loc);
        if (!this.isLoading() && this.mapInstance()) {
          const map = this.mapInstance();
          map?.flyTo(loc, map.getZoom());
        }
      }
    });

    effect(() => {
      const isSatellite = this.satelliteView();
      if (this.mapInstance()) {
        this.addTiles(isSatellite);
      }
    });

    effect(() => {
      const pos = this.position();
      const map = this.mapInstance();
      if (!pos || !map) return;

      if (!map.getCenter().equals(pos as any)) {
        map.flyTo(pos, map.getZoom());
      }
    });
  }

  ngAfterViewInit() {
    this.getMapKey();
  }

  ngOnDestroy() {
    const map = this.mapInstance();
    if (map) {
      if (map.gestureHandling?.enabled()) {
        map.gestureHandling.disable();
      }
      map.off();
      map.remove();
    }
  }

  private initMap() {
    L.Map.addInitHook('addHandler', 'scrollWheelZoom', (L as any).Map.ScrollWheelZoom);
    L.Map.addInitHook('addHandler', 'dragging', (L as any).Map.Drag);
    L.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling);

    delete (L.Icon.Default.prototype as any)._getIconUrl;

    const map = L.map('Map', {
      zoomControl: false,
      gestureHandling: this.isMobile(),
      gestureHandlingOptions: {
        text: {
          touch: this.i18n.langString('map_gesture_touch'),
          scroll: this.i18n.langString('map_gesture_scroll'),
          scrollMac: this.i18n.langString('map_gesture_scroll_mac'),
        },
        duration: 1500,
      },
    } as any);

    this.mapInstance.set(map);

    this.addTiles(this.satelliteView());

    map.on('zoom', (event) => {
      this.zoomLevel.set(event.target._zoom);
      this.updateMarkerPosition();
    });

    map.on('move', () => {
      this.updateMarkerPosition();
    });

    setTimeout(() => {
      map.invalidateSize();
      const center = this.center() || this.currentCenter();
      map.setView([center.lat, center.lng], map.getZoom() || 18);
      this.isLoading.set(false);
    }, 500);
  }

  private addTiles(isSatellite: boolean) {
    const map = this.mapInstance();
    if (!map) return;

    if (this.tileLayer) map.removeLayer(this.tileLayer);

    const map_access = isSatellite ? this.keys.satelliteView : this.keys.mapView;

    this.tileLayer = L.tileLayer(map_access, {
      attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/">OSM</a>',
      maxZoom: 18,
      tileSize: 512,
      zoomOffset: -1,
      accessToken: 'Bearer ' + this.storage.token(),
    } as any).addTo(map);
  }

  private addMarkers(
    showSensors: boolean,
    sensors: Sensor[],
    showUser: boolean,
    showAdded: boolean
  ) {
    const map = this.mapInstance();
    if (!map) return;

    if (this.markersLayer) map.removeLayer(this.markersLayer);
    this.markersLayer = L.layerGroup();

    if (showSensors) {
      sensors.forEach((sensor) => {
        if (sensor.geoPosition) {
          const coords = sensor.geoPosition.split(';');
          const lat = parseFloat(coords[0]);
          const lng = parseFloat(coords[1]);

          if (!isNaN(lat) && !isNaN(lng)) {
            const icon = L.divIcon({
              className: 'sensor-map-icon',
              html: '<img src="assets/icons/signal.svg">',
              iconSize: [40, 40],
              iconAnchor: [20, 20],
            });

            L.marker([lat, lng], { icon })
              .on('click', () => this.markerClick.emit(sensor))
              .addTo(this.markersLayer);
          }
        }
      });
    }

    if (showUser) this.userMarker.addTo(this.markersLayer);
    if (showAdded) this.addedMarker.addTo(this.markersLayer);

    this.markersLayer.addTo(map);
  }

  getMapKey() {
    const keys: any = {};
    this.assetService.getMapBoxKeys().then((res: any) => {
      this.keys = res;
      this.initMap();
      this.searchProvider = new MapBoxProvider({
        params: {
          access_token: keys.geoCodingToken,
          language: this.i18n.languageSignal(), // user language
        },
      });
      return this.searchProvider;
    });
  }

  private updateMarkerPosition() {
    const map = this.mapInstance();
    if (!map) return;

    const position = map.getCenter();
    this.addedMarker.setLatLng(position);
    this.positionChanged.emit({ lat: position.lat, lng: position.lng });
  }

  onZoomOut() {
    this.mapInstance()?.setZoom(this.mapInstance().getZoom() - 1);
  }

  onZoomIn() {
    this.mapInstance()?.setZoom(this.mapInstance().getZoom() + 1);
  }
}
